import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import { LOCALES, LOCALE_NAMES, LOCALE_SHORT } from '@/i18n/types';
import type { Locale } from '@/i18n/types';
import { cx } from '@/lib/cx';
import styles from './LanguageSwitcher.module.css';

/**
 * Picks the interface language.
 *
 * Each option is labelled in its OWN language — Қазақша, not "Kazakh" — because
 * the person looking for it may not read the one currently showing.
 *
 * Two renderings. In a bar — the header, the login card — a dropdown showing the
 * current three-letter form: the bar's width belongs to the navigation, and a
 * language is set once and left, so it does not need to be on show. In the burger
 * panel, the three as a segmented control: the panel has the room, and a dropdown
 * inside a dropdown is a worse answer than a row of three.
 */
export const LanguageSwitcher = ({ variant = 'menu' }: { variant?: 'menu' | 'segmented' }) =>
  variant === 'menu' ? <LanguageMenu /> : <LanguageSegments />;

/**
 * The dropdown. Its manners are ExportMenu's, which are the app's: outside
 * pointerdown closes, Escape closes and returns focus, and opening moves the
 * keyboard into the menu — onto the current language, so Tab goes on from there.
 *
 * The trigger's accessible name says what it is AND what is set, "Language:
 * Русский", since "РУС" alone tells a screen reader neither.
 */
const LanguageMenu = () => {
  const { locale, setLocale, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const currentRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    currentRef.current?.focus();

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const pick = (option: Locale) => {
    setIsOpen(false);
    triggerRef.current?.focus();
    setLocale(option);
  };

  return (
    <div className={styles.box} ref={boxRef}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.trigger}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`${t('language.label')}: ${LOCALE_NAMES[locale]}`}
        title={t('language.label')}
      >
        {LOCALE_SHORT[locale]}
        <ChevronDownIcon className={styles.caret} />
      </button>

      {isOpen && (
        <div className={styles.menu} role="menu" aria-label={t('language.label')}>
          {LOCALES.map((option) => {
            const selected = option === locale;

            return (
              <button
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                ref={selected ? currentRef : undefined}
                className={cx(styles.item, selected && styles.itemSelected)}
                onClick={() => pick(option)}
                lang={option}
              >
                {LOCALE_NAMES[option]}
                <span className={styles.itemShort} aria-hidden="true">
                  {LOCALE_SHORT[option]}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * The segmented control. The full name is the accessible name and the tooltip
 * while the button shows the three-letter form: the short label saves space, the
 * full one says what it actually is.
 *
 * `aria-pressed` marks the current choice, and the group is labelled, so a screen
 * reader announces three related toggles rather than three loose buttons.
 */
const LanguageSegments = () => {
  const { locale, setLocale, t } = useTranslation();

  return (
    <div className={styles.group} role="group" aria-label={t('language.label')}>
      {LOCALES.map((option) => {
        const selected = option === locale;

        return (
          <button
            key={option}
            type="button"
            className={cx(styles.option, selected && styles.selected)}
            onClick={() => setLocale(option)}
            aria-pressed={selected}
            aria-label={LOCALE_NAMES[option]}
            title={LOCALE_NAMES[option]}
            lang={option}
          >
            {LOCALE_SHORT[option]}
          </button>
        );
      })}
    </div>
  );
};
